/**
 * API Client - Abstraction layer for backend API calls
 * Can switch between real and mock implementations via factory function
 */

import { AxiosInstance } from 'axios';
import {
  IAuthApi,
  IAuthTokenApi,
  IUsersApi,
  ICustomersApi,
  IPropertiesApi,
  IItemsApi,
  IPriceChartsApi,
  ITaxRatesApi,
  IDiscountsApi,
  ILeadSourcesApi,
  IJobsApi,
  IInvoicesApi,
  IPaymentsApi,
  IGLAccountsApi,
  IJournalEntriesApi,
  IReportsApi,
  ICompanySettingsApi,
  IFinanceChargesApi,
  IFlatContractBillingApi,
  IAutoInvoicingApi,
  IDunningApi,
  IDispatchApi,
  IWorkOrdersApi,
  IRecurringJobsApi,
  IAuditLogsApi,
  IInventoryApi,
  INotificationsApi,
  IOnlinePaymentsApi,
  IChecklistTemplatesApi,
  IJobChecklistsApi,
  IAttachmentsApi,
  ITechnicianApi,
  IPortalApi,
  IRequestsApi,
  IEstimatesApi,
  IImportExportApi,
  IConversationsApi,
  IGeocodingApi,
  ISearchApi,
  IMfaApi,
  ISetupApi,
  IStaffNotificationsApi,
  ISurveysApi,
  IPortalRolloutApi,
  IBundlesApi,
  IStarterTemplatesApi,
  IRecurringPresetsApi,
} from './contracts';
import {
  HttpCore,
  AuthHttpApi,
  UsersHttpApi,
  CustomersHttpApi,
  PropertiesHttpApi,
  ItemsHttpApi,
  PriceChartsHttpApi,
  TaxRatesHttpApi,
  DiscountsHttpApi,
  LeadSourcesHttpApi,
  JobsHttpApi,
  InvoicesHttpApi,
  PaymentsHttpApi,
  GLAccountsHttpApi,
  JournalEntriesHttpApi,
  ReportsHttpApi,
  CompanySettingsHttpApi,
  FinanceChargesHttpApi,
  FlatContractBillingHttpApi,
  AutoInvoicingHttpApi,
  DunningHttpApi,
  DispatchHttpApi,
  WorkOrdersHttpApi,
  RecurringJobsHttpApi,
  AuditLogsHttpApi,
  InventoryHttpApi,
  NotificationsHttpApi,
  OnlinePaymentsHttpApi,
  ChecklistTemplatesHttpApi,
  JobChecklistsHttpApi,
  AttachmentsHttpApi,
  TechnicianHttpApi,
  PortalHttpApi,
  RequestsHttpApi,
  EstimatesHttpApi,
  ImportExportHttpApi,
  ConversationsHttpApi,
  GeocodingHttpApi,
  SearchHttpApi,
  MfaHttpApi,
  SetupHttpApi,
  StaffNotificationsHttpApi,
  SurveysHttpApi,
  PortalRolloutHttpApi,
  BundlesHttpApi,
  StarterTemplatesHttpApi,
  RecurringPresetsHttpApi,
} from './http';

export { CHANGE_PASSWORD_PATH } from './http';

/**
 * APIClient interface - the composition of every per-domain contract in
 * ./contracts. Implementation can be Real (HTTP) or Mock (in-memory).
 *
 * Consumers should depend on the narrowest contract(s) they actually use;
 * IAPIClient is for code that genuinely spans the whole API (the factory,
 * the mock, tests).
 */
export interface IAPIClient
  extends IAuthApi,
    IAuthTokenApi,
    IUsersApi,
    ICustomersApi,
    IPropertiesApi,
    IItemsApi,
    IPriceChartsApi,
    ITaxRatesApi,
    IDiscountsApi,
    ILeadSourcesApi,
    IJobsApi,
    IInvoicesApi,
    IPaymentsApi,
    IGLAccountsApi,
    IJournalEntriesApi,
    IReportsApi,
    ICompanySettingsApi,
    IFinanceChargesApi,
    IFlatContractBillingApi,
    IAutoInvoicingApi,
    IDunningApi,
    IDispatchApi,
    IWorkOrdersApi,
    IRecurringJobsApi,
    IAuditLogsApi,
    IInventoryApi,
    INotificationsApi,
    IOnlinePaymentsApi,
    IChecklistTemplatesApi,
    IJobChecklistsApi,
    IAttachmentsApi,
    ITechnicianApi,
    IPortalApi,
    IRequestsApi,
    IEstimatesApi,
    IImportExportApi,
    IConversationsApi,
    IGeocodingApi,
    ISearchApi,
    IMfaApi,
    ISetupApi,
    IStaffNotificationsApi,
    ISurveysApi,
    IPortalRolloutApi,
    IBundlesApi,
    IStarterTemplatesApi,
    IRecurringPresetsApi {}

/**
 * Real API Client - Makes actual HTTP requests via axios.
 *
 * A facade over the per-domain HTTP classes in ./http. All of them share this
 * client's single HttpCore (one axios instance, one bearer token), so a token
 * set here applies to every domain's requests.
 */
export class RealAPIClient implements IAPIClient {
  private readonly core: HttpCore;
  /** The shared axios instance every domain API sends through. */
  private readonly httpClient: AxiosInstance;
  private readonly authApi: AuthHttpApi;
  private readonly usersApi: UsersHttpApi;
  private readonly customersApi: CustomersHttpApi;
  private readonly propertiesApi: PropertiesHttpApi;
  private readonly itemsApi: ItemsHttpApi;
  private readonly priceChartsApi: PriceChartsHttpApi;
  private readonly taxRatesApi: TaxRatesHttpApi;
  private readonly discountsApi: DiscountsHttpApi;
  private readonly leadSourcesApi: LeadSourcesHttpApi;
  private readonly jobsApi: JobsHttpApi;
  private readonly invoicesApi: InvoicesHttpApi;
  private readonly paymentsApi: PaymentsHttpApi;
  private readonly glAccountsApi: GLAccountsHttpApi;
  private readonly journalEntriesApi: JournalEntriesHttpApi;
  private readonly reportsApi: ReportsHttpApi;
  private readonly companySettingsApi: CompanySettingsHttpApi;
  private readonly financeChargesApi: FinanceChargesHttpApi;
  private readonly flatContractBillingApi: FlatContractBillingHttpApi;
  private readonly autoInvoicingApi: AutoInvoicingHttpApi;
  private readonly dunningApi: DunningHttpApi;
  private readonly dispatchApi: DispatchHttpApi;
  private readonly workOrdersApi: WorkOrdersHttpApi;
  private readonly recurringJobsApi: RecurringJobsHttpApi;
  private readonly auditLogsApi: AuditLogsHttpApi;
  private readonly inventoryApi: InventoryHttpApi;
  private readonly notificationsApi: NotificationsHttpApi;
  private readonly onlinePaymentsApi: OnlinePaymentsHttpApi;
  private readonly checklistTemplatesApi: ChecklistTemplatesHttpApi;
  private readonly jobChecklistsApi: JobChecklistsHttpApi;
  private readonly attachmentsApi: AttachmentsHttpApi;
  private readonly technicianApi: TechnicianHttpApi;
  private readonly portalApi: PortalHttpApi;
  private readonly requestsApi: RequestsHttpApi;
  private readonly estimatesApi: EstimatesHttpApi;
  private readonly conversationsApi: ConversationsHttpApi;
  private readonly importExportApi: ImportExportHttpApi;
  private readonly geocodingApi: GeocodingHttpApi;
  private readonly searchApi: SearchHttpApi;
  private readonly mfaApi: MfaHttpApi;
  private readonly setupApi: SetupHttpApi;
  private readonly staffNotificationsApi: StaffNotificationsHttpApi;
  private readonly surveysApi: SurveysHttpApi;
  private readonly portalRolloutApi: PortalRolloutHttpApi;
  private readonly bundlesApi: BundlesHttpApi;
  private readonly starterApi: StarterTemplatesHttpApi;
  private readonly presetsApi: RecurringPresetsHttpApi;

  constructor(baseURL: string = 'http://localhost:5000/api') {
    this.core = new HttpCore(baseURL);
    this.httpClient = this.core.http;
    this.authApi = new AuthHttpApi(this.core);
    this.usersApi = new UsersHttpApi(this.core);
    this.customersApi = new CustomersHttpApi(this.core);
    this.propertiesApi = new PropertiesHttpApi(this.core);
    this.itemsApi = new ItemsHttpApi(this.core);
    this.priceChartsApi = new PriceChartsHttpApi(this.core);
    this.taxRatesApi = new TaxRatesHttpApi(this.core);
    this.discountsApi = new DiscountsHttpApi(this.core);
    this.leadSourcesApi = new LeadSourcesHttpApi(this.core);
    this.jobsApi = new JobsHttpApi(this.core);
    this.invoicesApi = new InvoicesHttpApi(this.core);
    this.paymentsApi = new PaymentsHttpApi(this.core);
    this.glAccountsApi = new GLAccountsHttpApi(this.core);
    this.journalEntriesApi = new JournalEntriesHttpApi(this.core);
    this.reportsApi = new ReportsHttpApi(this.core);
    this.companySettingsApi = new CompanySettingsHttpApi(this.core);
    this.financeChargesApi = new FinanceChargesHttpApi(this.core);
    this.flatContractBillingApi = new FlatContractBillingHttpApi(this.core);
    this.autoInvoicingApi = new AutoInvoicingHttpApi(this.core);
    this.dunningApi = new DunningHttpApi(this.core);
    this.dispatchApi = new DispatchHttpApi(this.core);
    this.workOrdersApi = new WorkOrdersHttpApi(this.core);
    this.recurringJobsApi = new RecurringJobsHttpApi(this.core);
    this.auditLogsApi = new AuditLogsHttpApi(this.core);
    this.inventoryApi = new InventoryHttpApi(this.core);
    this.notificationsApi = new NotificationsHttpApi(this.core);
    this.onlinePaymentsApi = new OnlinePaymentsHttpApi(this.core);
    this.checklistTemplatesApi = new ChecklistTemplatesHttpApi(this.core);
    this.jobChecklistsApi = new JobChecklistsHttpApi(this.core);
    this.attachmentsApi = new AttachmentsHttpApi(this.core);
    this.technicianApi = new TechnicianHttpApi(this.core);
    this.portalApi = new PortalHttpApi(this.core);
    this.requestsApi = new RequestsHttpApi(this.core);
    this.estimatesApi = new EstimatesHttpApi(this.core);
    this.conversationsApi = new ConversationsHttpApi(this.core);
    this.importExportApi = new ImportExportHttpApi(this.core);
    this.geocodingApi = new GeocodingHttpApi(this.core);
    this.searchApi = new SearchHttpApi(this.core);
    this.mfaApi = new MfaHttpApi(this.core);
    this.setupApi = new SetupHttpApi(this.core);
    this.staffNotificationsApi = new StaffNotificationsHttpApi(this.core);
    this.surveysApi = new SurveysHttpApi(this.core);
    this.portalRolloutApi = new PortalRolloutHttpApi(this.core);
    this.bundlesApi = new BundlesHttpApi(this.core);
    this.starterApi = new StarterTemplatesHttpApi(this.core);
    this.presetsApi = new RecurringPresetsHttpApi(this.core);
  }

  // IAuthTokenApi
  setAuthToken(token: string): void {
    this.core.setAuthToken(token);
  }

  getAuthToken(): string | null {
    return this.core.getAuthToken();
  }

  clearAuthToken(): void {
    this.core.clearAuthToken();
  }

  // IAuthApi
  login: IAuthApi['login'] = (...args) => this.authApi.login(...args);
  logout: IAuthApi['logout'] = (...args) => this.authApi.logout(...args);
  verifyToken: IAuthApi['verifyToken'] = (...args) => this.authApi.verifyToken(...args);
  verifyMfaLogin: IAuthApi['verifyMfaLogin'] = (...args) => this.authApi.verifyMfaLogin(...args);

  // IUsersApi
  listUsers: IUsersApi['listUsers'] = (...args) => this.usersApi.listUsers(...args);
  listAssignableUsers: IUsersApi['listAssignableUsers'] = (...args) => this.usersApi.listAssignableUsers(...args);
  getUser: IUsersApi['getUser'] = (...args) => this.usersApi.getUser(...args);
  createUser: IUsersApi['createUser'] = (...args) => this.usersApi.createUser(...args);
  updateUser: IUsersApi['updateUser'] = (...args) => this.usersApi.updateUser(...args);
  deactivateUser: IUsersApi['deactivateUser'] = (...args) => this.usersApi.deactivateUser(...args);
  resetUserPassword: IUsersApi['resetUserPassword'] = (...args) => this.usersApi.resetUserPassword(...args);
  changeOwnPassword: IUsersApi['changeOwnPassword'] = (...args) => this.usersApi.changeOwnPassword(...args);

  // ICustomersApi
  listCustomers: ICustomersApi['listCustomers'] = (...args) => this.customersApi.listCustomers(...args);
  getCustomer: ICustomersApi['getCustomer'] = (...args) => this.customersApi.getCustomer(...args);
  createCustomer: ICustomersApi['createCustomer'] = (...args) => this.customersApi.createCustomer(...args);
  updateCustomer: ICustomersApi['updateCustomer'] = (...args) => this.customersApi.updateCustomer(...args);

  // IPropertiesApi
  listProperties: IPropertiesApi['listProperties'] = (...args) => this.propertiesApi.listProperties(...args);
  getProperty: IPropertiesApi['getProperty'] = (...args) => this.propertiesApi.getProperty(...args);
  getPropertyBalance: IPropertiesApi['getPropertyBalance'] = (...args) => this.propertiesApi.getPropertyBalance(...args);
  createProperty: IPropertiesApi['createProperty'] = (...args) => this.propertiesApi.createProperty(...args);
  updateProperty: IPropertiesApi['updateProperty'] = (...args) => this.propertiesApi.updateProperty(...args);
  deleteProperty: IPropertiesApi['deleteProperty'] = (...args) => this.propertiesApi.deleteProperty(...args);

  // IItemsApi
  listItems: IItemsApi['listItems'] = (...args) => this.itemsApi.listItems(...args);
  getItem: IItemsApi['getItem'] = (...args) => this.itemsApi.getItem(...args);
  createItem: IItemsApi['createItem'] = (...args) => this.itemsApi.createItem(...args);
  updateItem: IItemsApi['updateItem'] = (...args) => this.itemsApi.updateItem(...args);
  deleteItem: IItemsApi['deleteItem'] = (...args) => this.itemsApi.deleteItem(...args);
  listItemCategories: IItemsApi['listItemCategories'] = (...args) => this.itemsApi.listItemCategories(...args);

  // IPriceChartsApi
  listPriceCharts: IPriceChartsApi['listPriceCharts'] = (...args) => this.priceChartsApi.listPriceCharts(...args);
  getPriceChart: IPriceChartsApi['getPriceChart'] = (...args) => this.priceChartsApi.getPriceChart(...args);
  createPriceChart: IPriceChartsApi['createPriceChart'] = (...args) => this.priceChartsApi.createPriceChart(...args);
  updatePriceChart: IPriceChartsApi['updatePriceChart'] = (...args) => this.priceChartsApi.updatePriceChart(...args);
  replacePriceChartTiers: IPriceChartsApi['replacePriceChartTiers'] = (...args) => this.priceChartsApi.replacePriceChartTiers(...args);
  deletePriceChart: IPriceChartsApi['deletePriceChart'] = (...args) => this.priceChartsApi.deletePriceChart(...args);
  listSizeUnits: IPriceChartsApi['listSizeUnits'] = (...args) => this.priceChartsApi.listSizeUnits(...args);
  quotePrice: IPriceChartsApi['quotePrice'] = (...args) => this.priceChartsApi.quotePrice(...args);

  // ITaxRatesApi
  listTaxRates: ITaxRatesApi['listTaxRates'] = (...args) => this.taxRatesApi.listTaxRates(...args);
  getTaxRate: ITaxRatesApi['getTaxRate'] = (...args) => this.taxRatesApi.getTaxRate(...args);
  createTaxRate: ITaxRatesApi['createTaxRate'] = (...args) => this.taxRatesApi.createTaxRate(...args);
  updateTaxRate: ITaxRatesApi['updateTaxRate'] = (...args) => this.taxRatesApi.updateTaxRate(...args);
  deleteTaxRate: ITaxRatesApi['deleteTaxRate'] = (...args) => this.taxRatesApi.deleteTaxRate(...args);

  // IDiscountsApi
  listDiscounts: IDiscountsApi['listDiscounts'] = (...args) => this.discountsApi.listDiscounts(...args);
  getDiscount: IDiscountsApi['getDiscount'] = (...args) => this.discountsApi.getDiscount(...args);
  createDiscount: IDiscountsApi['createDiscount'] = (...args) => this.discountsApi.createDiscount(...args);
  updateDiscount: IDiscountsApi['updateDiscount'] = (...args) => this.discountsApi.updateDiscount(...args);
  deleteDiscount: IDiscountsApi['deleteDiscount'] = (...args) => this.discountsApi.deleteDiscount(...args);
  listCustomerDiscounts: IDiscountsApi['listCustomerDiscounts'] = (...args) => this.discountsApi.listCustomerDiscounts(...args);
  attachCustomerDiscount: IDiscountsApi['attachCustomerDiscount'] = (...args) => this.discountsApi.attachCustomerDiscount(...args);
  detachCustomerDiscount: IDiscountsApi['detachCustomerDiscount'] = (...args) => this.discountsApi.detachCustomerDiscount(...args);
  listPropertyDiscounts: IDiscountsApi['listPropertyDiscounts'] = (...args) => this.discountsApi.listPropertyDiscounts(...args);
  attachPropertyDiscount: IDiscountsApi['attachPropertyDiscount'] = (...args) => this.discountsApi.attachPropertyDiscount(...args);
  detachPropertyDiscount: IDiscountsApi['detachPropertyDiscount'] = (...args) => this.discountsApi.detachPropertyDiscount(...args);

  // ILeadSourcesApi
  listLeadSources: ILeadSourcesApi['listLeadSources'] = (...args) => this.leadSourcesApi.listLeadSources(...args);
  getLeadSource: ILeadSourcesApi['getLeadSource'] = (...args) => this.leadSourcesApi.getLeadSource(...args);
  getLeadSourceEffectiveUtm: ILeadSourcesApi['getLeadSourceEffectiveUtm'] = (...args) => this.leadSourcesApi.getLeadSourceEffectiveUtm(...args);
  createLeadSource: ILeadSourcesApi['createLeadSource'] = (...args) => this.leadSourcesApi.createLeadSource(...args);
  updateLeadSource: ILeadSourcesApi['updateLeadSource'] = (...args) => this.leadSourcesApi.updateLeadSource(...args);
  deactivateLeadSource: ILeadSourcesApi['deactivateLeadSource'] = (...args) => this.leadSourcesApi.deactivateLeadSource(...args);
  deleteLeadSource: ILeadSourcesApi['deleteLeadSource'] = (...args) => this.leadSourcesApi.deleteLeadSource(...args);
  listCustomerLeadSourceHistory: ILeadSourcesApi['listCustomerLeadSourceHistory'] = (...args) => this.leadSourcesApi.listCustomerLeadSourceHistory(...args);

  // IJobsApi
  listJobs: IJobsApi['listJobs'] = (...args) => this.jobsApi.listJobs(...args);
  getJob: IJobsApi['getJob'] = (...args) => this.jobsApi.getJob(...args);
  createJob: IJobsApi['createJob'] = (...args) => this.jobsApi.createJob(...args);
  updateJob: IJobsApi['updateJob'] = (...args) => this.jobsApi.updateJob(...args);
  completeJob: IJobsApi['completeJob'] = (...args) => this.jobsApi.completeJob(...args);
  addJobLineItem: IJobsApi['addJobLineItem'] = (...args) => this.jobsApi.addJobLineItem(...args);
  updateJobLineItem: IJobsApi['updateJobLineItem'] = (...args) => this.jobsApi.updateJobLineItem(...args);
  removeJobLineItem: IJobsApi['removeJobLineItem'] = (...args) => this.jobsApi.removeJobLineItem(...args);

  // IInvoicesApi
  listInvoices: IInvoicesApi['listInvoices'] = (...args) => this.invoicesApi.listInvoices(...args);
  getInvoice: IInvoicesApi['getInvoice'] = (...args) => this.invoicesApi.getInvoice(...args);
  createInvoice: IInvoicesApi['createInvoice'] = (...args) => this.invoicesApi.createInvoice(...args);
  previewInvoice: IInvoicesApi['previewInvoice'] = (...args) => this.invoicesApi.previewInvoice(...args);
  updateInvoice: IInvoicesApi['updateInvoice'] = (...args) => this.invoicesApi.updateInvoice(...args);
  sendInvoice: IInvoicesApi['sendInvoice'] = (...args) => this.invoicesApi.sendInvoice(...args);
  cancelInvoice: IInvoicesApi['cancelInvoice'] = (...args) => this.invoicesApi.cancelInvoice(...args);
  regenerateInvoicePortalCode: IInvoicesApi['regenerateInvoicePortalCode'] = (...args) => this.invoicesApi.regenerateInvoicePortalCode(...args);

  // IPaymentsApi
  listPayments: IPaymentsApi['listPayments'] = (...args) => this.paymentsApi.listPayments(...args);
  getPayment: IPaymentsApi['getPayment'] = (...args) => this.paymentsApi.getPayment(...args);
  recordPayment: IPaymentsApi['recordPayment'] = (...args) => this.paymentsApi.recordPayment(...args);
  voidPayment: IPaymentsApi['voidPayment'] = (...args) => this.paymentsApi.voidPayment(...args);
  getPaymentGLEntries: IPaymentsApi['getPaymentGLEntries'] = (...args) => this.paymentsApi.getPaymentGLEntries(...args);
  getCreditTargets: IPaymentsApi['getCreditTargets'] = (...args) => this.paymentsApi.getCreditTargets(...args);
  applyCredit: IPaymentsApi['applyCredit'] = (...args) => this.paymentsApi.applyCredit(...args);
  unapplyCredit: IPaymentsApi['unapplyCredit'] = (...args) => this.paymentsApi.unapplyCredit(...args);
  getCustomerCredits: IPaymentsApi['getCustomerCredits'] = (...args) => this.paymentsApi.getCustomerCredits(...args);

  // IGLAccountsApi
  listGLAccounts: IGLAccountsApi['listGLAccounts'] = (...args) => this.glAccountsApi.listGLAccounts(...args);
  getGLAccount: IGLAccountsApi['getGLAccount'] = (...args) => this.glAccountsApi.getGLAccount(...args);
  createGLAccount: IGLAccountsApi['createGLAccount'] = (...args) => this.glAccountsApi.createGLAccount(...args);
  updateGLAccount: IGLAccountsApi['updateGLAccount'] = (...args) => this.glAccountsApi.updateGLAccount(...args);
  seedChartOfAccounts: IGLAccountsApi['seedChartOfAccounts'] = (...args) => this.glAccountsApi.seedChartOfAccounts(...args);

  // IJournalEntriesApi
  listJournalEntries: IJournalEntriesApi['listJournalEntries'] = (...args) => this.journalEntriesApi.listJournalEntries(...args);
  getJournalEntry: IJournalEntriesApi['getJournalEntry'] = (...args) => this.journalEntriesApi.getJournalEntry(...args);
  createJournalEntry: IJournalEntriesApi['createJournalEntry'] = (...args) => this.journalEntriesApi.createJournalEntry(...args);
  reverseJournalEntry: IJournalEntriesApi['reverseJournalEntry'] = (...args) => this.journalEntriesApi.reverseJournalEntry(...args);

  // IReportsApi
  getTrialBalance: IReportsApi['getTrialBalance'] = (...args) => this.reportsApi.getTrialBalance(...args);
  getARAging: IReportsApi['getARAging'] = (...args) => this.reportsApi.getARAging(...args);
  getRevenueReport: IReportsApi['getRevenueReport'] = (...args) => this.reportsApi.getRevenueReport(...args);
  getJobReport: IReportsApi['getJobReport'] = (...args) => this.reportsApi.getJobReport(...args);
  getPaymentReport: IReportsApi['getPaymentReport'] = (...args) => this.reportsApi.getPaymentReport(...args);
  getCustomerStatement: IReportsApi['getCustomerStatement'] = (...args) => this.reportsApi.getCustomerStatement(...args);

  // ICompanySettingsApi
  getCompanySettings: ICompanySettingsApi['getCompanySettings'] = (...args) => this.companySettingsApi.getCompanySettings(...args);
  updateCompanySettings: ICompanySettingsApi['updateCompanySettings'] = (...args) =>
    this.companySettingsApi.updateCompanySettings(...args);

  // IFinanceChargesApi
  previewFinanceCharges: IFinanceChargesApi['previewFinanceCharges'] = (...args) =>
    this.financeChargesApi.previewFinanceCharges(...args);
  assessFinanceCharges: IFinanceChargesApi['assessFinanceCharges'] = (...args) =>
    this.financeChargesApi.assessFinanceCharges(...args);

  // IFlatContractBillingApi
  previewFlatContractBilling: IFlatContractBillingApi['previewFlatContractBilling'] = (...args) =>
    this.flatContractBillingApi.previewFlatContractBilling(...args);
  billFlatContracts: IFlatContractBillingApi['billFlatContracts'] = (...args) =>
    this.flatContractBillingApi.billFlatContracts(...args);

  // IAutoInvoicingApi
  previewAutoInvoicing: IAutoInvoicingApi['previewAutoInvoicing'] = (...args) =>
    this.autoInvoicingApi.previewAutoInvoicing(...args);
  runAutoInvoicing: IAutoInvoicingApi['runAutoInvoicing'] = (...args) => this.autoInvoicingApi.runAutoInvoicing(...args);
  listAutoInvoicingRuns: IAutoInvoicingApi['listAutoInvoicingRuns'] = (...args) =>
    this.autoInvoicingApi.listAutoInvoicingRuns(...args);

  // IDispatchApi (tracker item 40)
  getDispatchBoard: IDispatchApi['getDispatchBoard'] = (...args) => this.dispatchApi.getDispatchBoard(...args);
  getDispatchBacklog: IDispatchApi['getDispatchBacklog'] = (...args) => this.dispatchApi.getDispatchBacklog(...args);
  checkJobSchedule: IDispatchApi['checkJobSchedule'] = (...args) => this.dispatchApi.checkJobSchedule(...args);
  scheduleJob: IDispatchApi['scheduleJob'] = (...args) => this.dispatchApi.scheduleJob(...args);
  getJobSuggestions: IDispatchApi['getJobSuggestions'] = (...args) => this.dispatchApi.getJobSuggestions(...args);
  getBulkSuggestions: IDispatchApi['getBulkSuggestions'] = (...args) => this.dispatchApi.getBulkSuggestions(...args);
  optimizeDay: IDispatchApi['optimizeDay'] = (...args) => this.dispatchApi.optimizeDay(...args);
  applyDayOptimization: IDispatchApi['applyDayOptimization'] = (...args) => this.dispatchApi.applyDayOptimization(...args);
  getTechnicianWorkHours: IDispatchApi['getTechnicianWorkHours'] = (...args) => this.dispatchApi.getTechnicianWorkHours(...args);
  setTechnicianWorkHours: IDispatchApi['setTechnicianWorkHours'] = (...args) => this.dispatchApi.setTechnicianWorkHours(...args);
  clearTechnicianWorkHours: IDispatchApi['clearTechnicianWorkHours'] = (...args) => this.dispatchApi.clearTechnicianWorkHours(...args);
  listTimeOff: IDispatchApi['listTimeOff'] = (...args) => this.dispatchApi.listTimeOff(...args);
  createTimeOff: IDispatchApi['createTimeOff'] = (...args) => this.dispatchApi.createTimeOff(...args);
  updateTimeOff: IDispatchApi['updateTimeOff'] = (...args) => this.dispatchApi.updateTimeOff(...args);
  deleteTimeOff: IDispatchApi['deleteTimeOff'] = (...args) => this.dispatchApi.deleteTimeOff(...args);
  listDispatchRoutes: IDispatchApi['listDispatchRoutes'] = (...args) => this.dispatchApi.listDispatchRoutes(...args);
  createDispatchRoute: IDispatchApi['createDispatchRoute'] = (...args) => this.dispatchApi.createDispatchRoute(...args);
  updateDispatchRoute: IDispatchApi['updateDispatchRoute'] = (...args) => this.dispatchApi.updateDispatchRoute(...args);
  deleteDispatchRoute: IDispatchApi['deleteDispatchRoute'] = (...args) => this.dispatchApi.deleteDispatchRoute(...args);
  getTechnicianRoutes: IDispatchApi['getTechnicianRoutes'] = (...args) => this.dispatchApi.getTechnicianRoutes(...args);
  setTechnicianRoutes: IDispatchApi['setTechnicianRoutes'] = (...args) => this.dispatchApi.setTechnicianRoutes(...args);
  listRouteAssignments: IDispatchApi['listRouteAssignments'] = (...args) => this.dispatchApi.listRouteAssignments(...args);
  createRouteAssignment: IDispatchApi['createRouteAssignment'] = (...args) => this.dispatchApi.createRouteAssignment(...args);
  deleteRouteAssignment: IDispatchApi['deleteRouteAssignment'] = (...args) => this.dispatchApi.deleteRouteAssignment(...args);
  setRouteDefault: IDispatchApi['setRouteDefault'] = (...args) => this.dispatchApi.setRouteDefault(...args);

  // IWorkOrdersApi (tracker item 58)
  listWorkOrderCandidates: IWorkOrdersApi['listWorkOrderCandidates'] = (...args) => this.workOrdersApi.listWorkOrderCandidates(...args);
  resolveWorkOrderSelection: IWorkOrdersApi['resolveWorkOrderSelection'] = (...args) => this.workOrdersApi.resolveWorkOrderSelection(...args);
  prepareWorkOrderBatch: IWorkOrdersApi['prepareWorkOrderBatch'] = (...args) => this.workOrdersApi.prepareWorkOrderBatch(...args);
  listWorkOrderBatches: IWorkOrdersApi['listWorkOrderBatches'] = (...args) => this.workOrdersApi.listWorkOrderBatches(...args);
  getWorkOrderBatch: IWorkOrdersApi['getWorkOrderBatch'] = (...args) => this.workOrdersApi.getWorkOrderBatch(...args);
  recordWorkOrderPrint: IWorkOrdersApi['recordWorkOrderPrint'] = (...args) => this.workOrdersApi.recordWorkOrderPrint(...args);
  confirmWorkOrderJobs: IWorkOrdersApi['confirmWorkOrderJobs'] = (...args) => this.workOrdersApi.confirmWorkOrderJobs(...args);

  // IDunningApi
  previewDunning: IDunningApi['previewDunning'] = (...args) => this.dunningApi.previewDunning(...args);
  runDunning: IDunningApi['runDunning'] = (...args) => this.dunningApi.runDunning(...args);
  listDunningRuns: IDunningApi['listDunningRuns'] = (...args) => this.dunningApi.listDunningRuns(...args);
  getDunningSteps: IDunningApi['getDunningSteps'] = (...args) => this.dunningApi.getDunningSteps(...args);
  replaceDunningSteps: IDunningApi['replaceDunningSteps'] = (...args) => this.dunningApi.replaceDunningSteps(...args);
  listInvoiceDunningNotices: IDunningApi['listInvoiceDunningNotices'] = (...args) =>
    this.dunningApi.listInvoiceDunningNotices(...args);
  setInvoiceDunningHold: IDunningApi['setInvoiceDunningHold'] = (...args) => this.dunningApi.setInvoiceDunningHold(...args);

  // IRecurringJobsApi
  listRecurringSchedules: IRecurringJobsApi['listRecurringSchedules'] = (...args) =>
    this.recurringJobsApi.listRecurringSchedules(...args);
  getRecurringSchedule: IRecurringJobsApi['getRecurringSchedule'] = (...args) => this.recurringJobsApi.getRecurringSchedule(...args);
  createRecurringSchedule: IRecurringJobsApi['createRecurringSchedule'] = (...args) =>
    this.recurringJobsApi.createRecurringSchedule(...args);
  updateRecurringSchedule: IRecurringJobsApi['updateRecurringSchedule'] = (...args) =>
    this.recurringJobsApi.updateRecurringSchedule(...args);
  deleteRecurringSchedule: IRecurringJobsApi['deleteRecurringSchedule'] = (...args) =>
    this.recurringJobsApi.deleteRecurringSchedule(...args);
  generateRecurringJobs: IRecurringJobsApi['generateRecurringJobs'] = (...args) => this.recurringJobsApi.generateRecurringJobs(...args);
  previewRecurringOccurrences: IRecurringJobsApi['previewRecurringOccurrences'] = (...args) =>
    this.recurringJobsApi.previewRecurringOccurrences(...args);
  addRecurringSkippedDate: IRecurringJobsApi['addRecurringSkippedDate'] = (...args) =>
    this.recurringJobsApi.addRecurringSkippedDate(...args);
  removeRecurringSkippedDate: IRecurringJobsApi['removeRecurringSkippedDate'] = (...args) =>
    this.recurringJobsApi.removeRecurringSkippedDate(...args);

  // IAuditLogsApi
  listAuditLogs: IAuditLogsApi['listAuditLogs'] = (...args) => this.auditLogsApi.listAuditLogs(...args);
  listAuditLogActions: IAuditLogsApi['listAuditLogActions'] = (...args) => this.auditLogsApi.listAuditLogActions(...args);

  // IInventoryApi
  receiveStock: IInventoryApi['receiveStock'] = (...args) => this.inventoryApi.receiveStock(...args);
  adjustStock: IInventoryApi['adjustStock'] = (...args) => this.inventoryApi.adjustStock(...args);
  listInventoryAdjustments: IInventoryApi['listInventoryAdjustments'] = (...args) =>
    this.inventoryApi.listInventoryAdjustments(...args);
  getInventoryValuation: IInventoryApi['getInventoryValuation'] = (...args) => this.inventoryApi.getInventoryValuation(...args);

  // INotificationsApi
  listNotificationTemplates: INotificationsApi['listNotificationTemplates'] = (...args) => this.notificationsApi.listNotificationTemplates(...args);
  updateNotificationTemplate: INotificationsApi['updateNotificationTemplate'] = (...args) => this.notificationsApi.updateNotificationTemplate(...args);
  previewNotificationTemplate: INotificationsApi['previewNotificationTemplate'] = (...args) => this.notificationsApi.previewNotificationTemplate(...args);
  listOutbox: INotificationsApi['listOutbox'] = (...args) => this.notificationsApi.listOutbox(...args);
  retryOutboxMessage: INotificationsApi['retryOutboxMessage'] = (...args) => this.notificationsApi.retryOutboxMessage(...args);
  cancelOutboxMessage: INotificationsApi['cancelOutboxMessage'] = (...args) => this.notificationsApi.cancelOutboxMessage(...args);
  emailInvoice: INotificationsApi['emailInvoice'] = (...args) => this.notificationsApi.emailInvoice(...args);
  emailStatement: INotificationsApi['emailStatement'] = (...args) => this.notificationsApi.emailStatement(...args);
  sendTestNotification: INotificationsApi['sendTestNotification'] = (...args) => this.notificationsApi.sendTestNotification(...args);
  sendTestNotificationTemplate: INotificationsApi['sendTestNotificationTemplate'] = (...args) => this.notificationsApi.sendTestNotificationTemplate(...args);
  listNotificationProviders: INotificationsApi['listNotificationProviders'] = (...args) => this.notificationsApi.listNotificationProviders(...args);
  getNotificationProvider: INotificationsApi['getNotificationProvider'] = (...args) => this.notificationsApi.getNotificationProvider(...args);
  saveNotificationProvider: INotificationsApi['saveNotificationProvider'] = (...args) => this.notificationsApi.saveNotificationProvider(...args);
  verifyNotificationProvider: INotificationsApi['verifyNotificationProvider'] = (...args) => this.notificationsApi.verifyNotificationProvider(...args);
  getNotificationSettings: INotificationsApi['getNotificationSettings'] = (...args) => this.notificationsApi.getNotificationSettings(...args);
  updateNotificationSettings: INotificationsApi['updateNotificationSettings'] = (...args) => this.notificationsApi.updateNotificationSettings(...args);
  // Day-of customer messages on a job (tracker item 43)
  getJobCustomerNotifications: INotificationsApi['getJobCustomerNotifications'] = (...args) => this.notificationsApi.getJobCustomerNotifications(...args);
  sendJobCustomerNotification: INotificationsApi['sendJobCustomerNotification'] = (...args) => this.notificationsApi.sendJobCustomerNotification(...args);
  getPublicInvoiceView: INotificationsApi['getPublicInvoiceView'] = (...args) => this.notificationsApi.getPublicInvoiceView(...args);
  getPublicStatementView: INotificationsApi['getPublicStatementView'] = (...args) => this.notificationsApi.getPublicStatementView(...args);

  // IOnlinePaymentsApi (tracker item 38)
  getProcessorConnection: IOnlinePaymentsApi['getProcessorConnection'] = (...args) => this.onlinePaymentsApi.getProcessorConnection(...args);
  startProcessorConnect: IOnlinePaymentsApi['startProcessorConnect'] = (...args) => this.onlinePaymentsApi.startProcessorConnect(...args);
  chooseProcessorLocation: IOnlinePaymentsApi['chooseProcessorLocation'] = (...args) => this.onlinePaymentsApi.chooseProcessorLocation(...args);
  disconnectProcessor: IOnlinePaymentsApi['disconnectProcessor'] = (...args) => this.onlinePaymentsApi.disconnectProcessor(...args);
  getOnlinePaymentSettings: IOnlinePaymentsApi['getOnlinePaymentSettings'] = (...args) => this.onlinePaymentsApi.getOnlinePaymentSettings(...args);
  updateOnlinePaymentSettings: IOnlinePaymentsApi['updateOnlinePaymentSettings'] = (...args) => this.onlinePaymentsApi.updateOnlinePaymentSettings(...args);
  getPublicPayConfig: IOnlinePaymentsApi['getPublicPayConfig'] = (...args) => this.onlinePaymentsApi.getPublicPayConfig(...args);
  payPublicInvoice: IOnlinePaymentsApi['payPublicInvoice'] = (...args) => this.onlinePaymentsApi.payPublicInvoice(...args);
  listPaymentMethods: IOnlinePaymentsApi['listPaymentMethods'] = (...args) => this.onlinePaymentsApi.listPaymentMethods(...args);
  addPaymentMethod: IOnlinePaymentsApi['addPaymentMethod'] = (...args) => this.onlinePaymentsApi.addPaymentMethod(...args);
  setPaymentMethodAutopay: IOnlinePaymentsApi['setPaymentMethodAutopay'] = (...args) => this.onlinePaymentsApi.setPaymentMethodAutopay(...args);
  setDefaultPaymentMethod: IOnlinePaymentsApi['setDefaultPaymentMethod'] = (...args) => this.onlinePaymentsApi.setDefaultPaymentMethod(...args);
  removePaymentMethod: IOnlinePaymentsApi['removePaymentMethod'] = (...args) => this.onlinePaymentsApi.removePaymentMethod(...args);
  chargeInvoiceCard: IOnlinePaymentsApi['chargeInvoiceCard'] = (...args) => this.onlinePaymentsApi.chargeInvoiceCard(...args);
  refundPayment: IOnlinePaymentsApi['refundPayment'] = (...args) => this.onlinePaymentsApi.refundPayment(...args);
  listOnlinePaymentAttempts: IOnlinePaymentsApi['listOnlinePaymentAttempts'] = (...args) => this.onlinePaymentsApi.listOnlinePaymentAttempts(...args);
  reconcileOnlinePaymentAttempt: IOnlinePaymentsApi['reconcileOnlinePaymentAttempt'] = (...args) => this.onlinePaymentsApi.reconcileOnlinePaymentAttempt(...args);

  // IChecklistTemplatesApi (tracker item 44)
  listChecklistTemplates: IChecklistTemplatesApi['listChecklistTemplates'] = (...args) => this.checklistTemplatesApi.listChecklistTemplates(...args);
  getChecklistTemplate: IChecklistTemplatesApi['getChecklistTemplate'] = (...args) => this.checklistTemplatesApi.getChecklistTemplate(...args);
  createChecklistTemplate: IChecklistTemplatesApi['createChecklistTemplate'] = (...args) => this.checklistTemplatesApi.createChecklistTemplate(...args);
  replaceChecklistTemplate: IChecklistTemplatesApi['replaceChecklistTemplate'] = (...args) => this.checklistTemplatesApi.replaceChecklistTemplate(...args);
  deleteChecklistTemplate: IChecklistTemplatesApi['deleteChecklistTemplate'] = (...args) => this.checklistTemplatesApi.deleteChecklistTemplate(...args);

  // IJobChecklistsApi (tracker item 44)
  getJobChecklist: IJobChecklistsApi['getJobChecklist'] = (...args) => this.jobChecklistsApi.getJobChecklist(...args);
  addJobChecklistStep: IJobChecklistsApi['addJobChecklistStep'] = (...args) => this.jobChecklistsApi.addJobChecklistStep(...args);
  updateJobChecklistStep: IJobChecklistsApi['updateJobChecklistStep'] = (...args) => this.jobChecklistsApi.updateJobChecklistStep(...args);
  deleteJobChecklistStep: IJobChecklistsApi['deleteJobChecklistStep'] = (...args) => this.jobChecklistsApi.deleteJobChecklistStep(...args);
  completeJobChecklistStep: IJobChecklistsApi['completeJobChecklistStep'] = (...args) => this.jobChecklistsApi.completeJobChecklistStep(...args);
  uncompleteJobChecklistStep: IJobChecklistsApi['uncompleteJobChecklistStep'] = (...args) => this.jobChecklistsApi.uncompleteJobChecklistStep(...args);
  applyJobChecklistTemplate: IJobChecklistsApi['applyJobChecklistTemplate'] = (...args) => this.jobChecklistsApi.applyJobChecklistTemplate(...args);

  // IImportExportApi (tracker item 52)
  listImportSpecs: IImportExportApi['listImportSpecs'] = (...args) => this.importExportApi.listImportSpecs(...args);
  downloadImportTemplate: IImportExportApi['downloadImportTemplate'] = (...args) => this.importExportApi.downloadImportTemplate(...args);
  validateImport: IImportExportApi['validateImport'] = (...args) => this.importExportApi.validateImport(...args);
  commitImport: IImportExportApi['commitImport'] = (...args) => this.importExportApi.commitImport(...args);
  downloadExport: IImportExportApi['downloadExport'] = (...args) => this.importExportApi.downloadExport(...args);

  // IAttachmentsApi (tracker item 47)
  listAttachments: IAttachmentsApi['listAttachments'] = (...args) => this.attachmentsApi.listAttachments(...args);
  uploadAttachment: IAttachmentsApi['uploadAttachment'] = (...args) => this.attachmentsApi.uploadAttachment(...args);
  getAttachment: IAttachmentsApi['getAttachment'] = (...args) => this.attachmentsApi.getAttachment(...args);
  downloadAttachment: IAttachmentsApi['downloadAttachment'] = (...args) => this.attachmentsApi.downloadAttachment(...args);
  updateAttachment: IAttachmentsApi['updateAttachment'] = (...args) => this.attachmentsApi.updateAttachment(...args);
  deleteAttachment: IAttachmentsApi['deleteAttachment'] = (...args) => this.attachmentsApi.deleteAttachment(...args);

  // ITechnicianApi (tracker item 42)
  getTechnicianToday: ITechnicianApi['getTechnicianToday'] = (...args) => this.technicianApi.getTechnicianToday(...args);
  getTechnicianJob: ITechnicianApi['getTechnicianJob'] = (...args) => this.technicianApi.getTechnicianJob(...args);
  startTechnicianJob: ITechnicianApi['startTechnicianJob'] = (...args) => this.technicianApi.startTechnicianJob(...args);
  stopTechnicianJob: ITechnicianApi['stopTechnicianJob'] = (...args) => this.technicianApi.stopTechnicianJob(...args);
  finishTechnicianJob: ITechnicianApi['finishTechnicianJob'] = (...args) => this.technicianApi.finishTechnicianJob(...args);
  addTechnicianNote: ITechnicianApi['addTechnicianNote'] = (...args) => this.technicianApi.addTechnicianNote(...args);
  uploadTechnicianSignature: ITechnicianApi['uploadTechnicianSignature'] = (...args) => this.technicianApi.uploadTechnicianSignature(...args);
  listTechnicianSignatures: ITechnicianApi['listTechnicianSignatures'] = (...args) => this.technicianApi.listTechnicianSignatures(...args);
  downloadTechnicianSignatureImage: ITechnicianApi['downloadTechnicianSignatureImage'] = (...args) => this.technicianApi.downloadTechnicianSignatureImage(...args);

  // IPortalApi (tracker item 45)
  registerPortalAccount: IPortalApi['registerPortalAccount'] = (...args) => this.portalApi.registerPortalAccount(...args);
  verifyPortalEmail: IPortalApi['verifyPortalEmail'] = (...args) => this.portalApi.verifyPortalEmail(...args);
  acceptPortalInvite: IPortalApi['acceptPortalInvite'] = (...args) => this.portalApi.acceptPortalInvite(...args);
  requestPortalPasswordReset: IPortalApi['requestPortalPasswordReset'] = (...args) => this.portalApi.requestPortalPasswordReset(...args);
  confirmPortalPasswordReset: IPortalApi['confirmPortalPasswordReset'] = (...args) => this.portalApi.confirmPortalPasswordReset(...args);
  getPortalMe: IPortalApi['getPortalMe'] = (...args) => this.portalApi.getPortalMe(...args);
  listPortalInvoices: IPortalApi['listPortalInvoices'] = (...args) => this.portalApi.listPortalInvoices(...args);
  getPortalInvoice: IPortalApi['getPortalInvoice'] = (...args) => this.portalApi.getPortalInvoice(...args);
  listPortalJobs: IPortalApi['listPortalJobs'] = (...args) => this.portalApi.listPortalJobs(...args);
  getPortalJob: IPortalApi['getPortalJob'] = (...args) => this.portalApi.getPortalJob(...args);
  getPortalStatement: IPortalApi['getPortalStatement'] = (...args) => this.portalApi.getPortalStatement(...args);
  getPortalProfile: IPortalApi['getPortalProfile'] = (...args) => this.portalApi.getPortalProfile(...args);
  updatePortalProfile: IPortalApi['updatePortalProfile'] = (...args) => this.portalApi.updatePortalProfile(...args);
  getPortalPayConfig: IPortalApi['getPortalPayConfig'] = (...args) => this.portalApi.getPortalPayConfig(...args);
  payPortalInvoice: IPortalApi['payPortalInvoice'] = (...args) => this.portalApi.payPortalInvoice(...args);
  getPortalPaymentMethods: IPortalApi['getPortalPaymentMethods'] = (...args) => this.portalApi.getPortalPaymentMethods(...args);
  addPortalPaymentMethod: IPortalApi['addPortalPaymentMethod'] = (...args) => this.portalApi.addPortalPaymentMethod(...args);
  removePortalPaymentMethod: IPortalApi['removePortalPaymentMethod'] = (...args) => this.portalApi.removePortalPaymentMethod(...args);
  setPortalAutopay: IPortalApi['setPortalAutopay'] = (...args) => this.portalApi.setPortalAutopay(...args);

  // IPortalApi, phase 4 (requests)
  listPortalRequestableItems: IPortalApi['listPortalRequestableItems'] = (...args) => this.portalApi.listPortalRequestableItems(...args);
  createPortalServiceRequest: IPortalApi['createPortalServiceRequest'] = (...args) => this.portalApi.createPortalServiceRequest(...args);
  listPortalServiceRequests: IPortalApi['listPortalServiceRequests'] = (...args) => this.portalApi.listPortalServiceRequests(...args);
  getPortalServiceRequest: IPortalApi['getPortalServiceRequest'] = (...args) => this.portalApi.getPortalServiceRequest(...args);
  createPortalRescheduleRequest: IPortalApi['createPortalRescheduleRequest'] = (...args) => this.portalApi.createPortalRescheduleRequest(...args);
  listPortalRescheduleRequests: IPortalApi['listPortalRescheduleRequests'] = (...args) => this.portalApi.listPortalRescheduleRequests(...args);
  cancelPortalRescheduleRequest: IPortalApi['cancelPortalRescheduleRequest'] = (...args) => this.portalApi.cancelPortalRescheduleRequest(...args);
  listPortalRequestableBundles: IPortalApi['listPortalRequestableBundles'] = (...args) => this.portalApi.listPortalRequestableBundles(...args);
  createPortalBundleRequest: IPortalApi['createPortalBundleRequest'] = (...args) => this.portalApi.createPortalBundleRequest(...args);
  listPortalEnrollments: IPortalApi['listPortalEnrollments'] = (...args) => this.portalApi.listPortalEnrollments(...args);
  getPortalEnrollment: IPortalApi['getPortalEnrollment'] = (...args) => this.portalApi.getPortalEnrollment(...args);
  setPortalEnrollmentAutoRenew: IPortalApi['setPortalEnrollmentAutoRenew'] = (...args) => this.portalApi.setPortalEnrollmentAutoRenew(...args);

  // IRequestsApi (tracker item 45, office side)
  listServiceRequests: IRequestsApi['listServiceRequests'] = (...args) => this.requestsApi.listServiceRequests(...args);
  getServiceRequest: IRequestsApi['getServiceRequest'] = (...args) => this.requestsApi.getServiceRequest(...args);
  convertServiceRequest: IRequestsApi['convertServiceRequest'] = (...args) => this.requestsApi.convertServiceRequest(...args);
  declineServiceRequest: IRequestsApi['declineServiceRequest'] = (...args) => this.requestsApi.declineServiceRequest(...args);
  listRescheduleRequests: IRequestsApi['listRescheduleRequests'] = (...args) => this.requestsApi.listRescheduleRequests(...args);
  approveRescheduleRequest: IRequestsApi['approveRescheduleRequest'] = (...args) => this.requestsApi.approveRescheduleRequest(...args);
  declineRescheduleRequest: IRequestsApi['declineRescheduleRequest'] = (...args) => this.requestsApi.declineRescheduleRequest(...args);
  listPortalAccessRequests: IRequestsApi['listPortalAccessRequests'] = (...args) => this.requestsApi.listPortalAccessRequests(...args);
  decidePortalAccessRequest: IRequestsApi['decidePortalAccessRequest'] = (...args) => this.requestsApi.decidePortalAccessRequest(...args);
  listCustomerPortalLogins: IRequestsApi['listCustomerPortalLogins'] = (...args) => this.requestsApi.listCustomerPortalLogins(...args);
  inviteCustomerPortalUser: IRequestsApi['inviteCustomerPortalUser'] = (...args) => this.requestsApi.inviteCustomerPortalUser(...args);
  resendCustomerPortalInvite: IRequestsApi['resendCustomerPortalInvite'] = (...args) => this.requestsApi.resendCustomerPortalInvite(...args);
  disableCustomerPortalUser: IRequestsApi['disableCustomerPortalUser'] = (...args) => this.requestsApi.disableCustomerPortalUser(...args);
  unlinkCustomerPortalUser: IRequestsApi['unlinkCustomerPortalUser'] = (...args) => this.requestsApi.unlinkCustomerPortalUser(...args);
  // Web form intake (tracker item 49)
  getWebFormIntake: IRequestsApi['getWebFormIntake'] = (...args) => this.requestsApi.getWebFormIntake(...args);
  updateWebFormIntake: IRequestsApi['updateWebFormIntake'] = (...args) => this.requestsApi.updateWebFormIntake(...args);
  regenerateWebFormKey: IRequestsApi['regenerateWebFormKey'] = (...args) => this.requestsApi.regenerateWebFormKey(...args);
  getWebFormItems: IRequestsApi['getWebFormItems'] = (...args) => this.requestsApi.getWebFormItems(...args);
  submitWebFormRequest: IRequestsApi['submitWebFormRequest'] = (...args) => this.requestsApi.submitWebFormRequest(...args);

  // IConversationsApi (tracker item 56)
  listConversations: IConversationsApi['listConversations'] = (...args) => this.conversationsApi.listConversations(...args);
  getConversationUnreadCount: IConversationsApi['getConversationUnreadCount'] = (...args) => this.conversationsApi.getConversationUnreadCount(...args);
  getConversation: IConversationsApi['getConversation'] = (...args) => this.conversationsApi.getConversation(...args);
  createConversation: IConversationsApi['createConversation'] = (...args) => this.conversationsApi.createConversation(...args);
  replyToConversation: IConversationsApi['replyToConversation'] = (...args) => this.conversationsApi.replyToConversation(...args);
  addConversationNote: IConversationsApi['addConversationNote'] = (...args) => this.conversationsApi.addConversationNote(...args);
  updateConversation: IConversationsApi['updateConversation'] = (...args) => this.conversationsApi.updateConversation(...args);
  markConversationRead: IConversationsApi['markConversationRead'] = (...args) => this.conversationsApi.markConversationRead(...args);
  listUnmatchedConversations: IConversationsApi['listUnmatchedConversations'] = (...args) => this.conversationsApi.listUnmatchedConversations(...args);
  assignConversationCustomer: IConversationsApi['assignConversationCustomer'] = (...args) => this.conversationsApi.assignConversationCustomer(...args);
  discardConversation: IConversationsApi['discardConversation'] = (...args) => this.conversationsApi.discardConversation(...args);
  getPortalMessages: IConversationsApi['getPortalMessages'] = (...args) => this.conversationsApi.getPortalMessages(...args);
  sendPortalMessage: IConversationsApi['sendPortalMessage'] = (...args) => this.conversationsApi.sendPortalMessage(...args);
  markPortalMessagesRead: IConversationsApi['markPortalMessagesRead'] = (...args) => this.conversationsApi.markPortalMessagesRead(...args);

  // IGeocodingApi (tracker item 41)
  setPropertyLocation: IGeocodingApi['setPropertyLocation'] = (...args) => this.geocodingApi.setPropertyLocation(...args);
  requeuePropertyGeocode: IGeocodingApi['requeuePropertyGeocode'] = (...args) => this.geocodingApi.requeuePropertyGeocode(...args);
  getGeocodingStatus: IGeocodingApi['getGeocodingStatus'] = (...args) => this.geocodingApi.getGeocodingStatus(...args);
  retryFailedGeocoding: IGeocodingApi['retryFailedGeocoding'] = (...args) => this.geocodingApi.retryFailedGeocoding(...args);

  // ISearchApi (tracker item 61)
  globalSearch: ISearchApi['globalSearch'] = (...args) => this.searchApi.globalSearch(...args);

  // IMfaApi (tracker item 63)
  getMfaStatus: IMfaApi['getMfaStatus'] = (...args) => this.mfaApi.getMfaStatus(...args);
  startMfaEnrollment: IMfaApi['startMfaEnrollment'] = (...args) => this.mfaApi.startMfaEnrollment(...args);
  confirmMfaEnrollment: IMfaApi['confirmMfaEnrollment'] = (...args) => this.mfaApi.confirmMfaEnrollment(...args);
  disableMfa: IMfaApi['disableMfa'] = (...args) => this.mfaApi.disableMfa(...args);
  regenerateMfaRecoveryCodes: IMfaApi['regenerateMfaRecoveryCodes'] = (...args) => this.mfaApi.regenerateMfaRecoveryCodes(...args);
  getMfaPolicy: IMfaApi['getMfaPolicy'] = (...args) => this.mfaApi.getMfaPolicy(...args);
  updateMfaPolicy: IMfaApi['updateMfaPolicy'] = (...args) => this.mfaApi.updateMfaPolicy(...args);
  listUserMfaStatuses: IMfaApi['listUserMfaStatuses'] = (...args) => this.mfaApi.listUserMfaStatuses(...args);
  resetUserMfa: IMfaApi['resetUserMfa'] = (...args) => this.mfaApi.resetUserMfa(...args);

  // ISetupApi (tracker item 75)
  getSetup: ISetupApi['getSetup'] = (...args) => this.setupApi.getSetup(...args);
  skipSetupStep: ISetupApi['skipSetupStep'] = (...args) => this.setupApi.skipSetupStep(...args);
  unskipSetupStep: ISetupApi['unskipSetupStep'] = (...args) => this.setupApi.unskipSetupStep(...args);
  markSetupStepDone: ISetupApi['markSetupStepDone'] = (...args) => this.setupApi.markSetupStepDone(...args);
  unmarkSetupStepDone: ISetupApi['unmarkSetupStepDone'] = (...args) => this.setupApi.unmarkSetupStepDone(...args);
  setSetupDismissed: ISetupApi['setSetupDismissed'] = (...args) => this.setupApi.setSetupDismissed(...args);

  // IStaffNotificationsApi (tracker item 62)
  listStaffNotifications: IStaffNotificationsApi['listStaffNotifications'] = (...args) => this.staffNotificationsApi.listStaffNotifications(...args);
  getStaffNotificationUnreadCount: IStaffNotificationsApi['getStaffNotificationUnreadCount'] = (...args) => this.staffNotificationsApi.getStaffNotificationUnreadCount(...args);
  markStaffNotificationRead: IStaffNotificationsApi['markStaffNotificationRead'] = (...args) => this.staffNotificationsApi.markStaffNotificationRead(...args);
  markAllStaffNotificationsRead: IStaffNotificationsApi['markAllStaffNotificationsRead'] = (...args) => this.staffNotificationsApi.markAllStaffNotificationsRead(...args);
  getStaffNotificationPreferences: IStaffNotificationsApi['getStaffNotificationPreferences'] = (...args) => this.staffNotificationsApi.getStaffNotificationPreferences(...args);
  setStaffNotificationPreferences: IStaffNotificationsApi['setStaffNotificationPreferences'] = (...args) => this.staffNotificationsApi.setStaffNotificationPreferences(...args);

  // ISurveysApi (tracker item 69)
  getSurveySettings: ISurveysApi['getSurveySettings'] = (...args) => this.surveysApi.getSurveySettings(...args);
  updateSurveySettings: ISurveysApi['updateSurveySettings'] = (...args) => this.surveysApi.updateSurveySettings(...args);
  listSurveyResponses: ISurveysApi['listSurveyResponses'] = (...args) => this.surveysApi.listSurveyResponses(...args);
  getJobSurvey: ISurveysApi['getJobSurvey'] = (...args) => this.surveysApi.getJobSurvey(...args);
  sendJobSurvey: ISurveysApi['sendJobSurvey'] = (...args) => this.surveysApi.sendJobSurvey(...args);
  getSatisfactionReport: ISurveysApi['getSatisfactionReport'] = (...args) => this.surveysApi.getSatisfactionReport(...args);
  getPublicSurvey: ISurveysApi['getPublicSurvey'] = (...args) => this.surveysApi.getPublicSurvey(...args);
  submitPublicSurvey: ISurveysApi['submitPublicSurvey'] = (...args) => this.surveysApi.submitPublicSurvey(...args);

  // IPortalRolloutApi (tracker item 81)
  getPortalRolloutReadiness: IPortalRolloutApi['getPortalRolloutReadiness'] = (...args) => this.portalRolloutApi.getPortalRolloutReadiness(...args);
  previewPortalRollout: IPortalRolloutApi['previewPortalRollout'] = (...args) => this.portalRolloutApi.previewPortalRollout(...args);
  createPortalRolloutCampaign: IPortalRolloutApi['createPortalRolloutCampaign'] = (...args) => this.portalRolloutApi.createPortalRolloutCampaign(...args);
  listPortalRolloutCampaigns: IPortalRolloutApi['listPortalRolloutCampaigns'] = (...args) => this.portalRolloutApi.listPortalRolloutCampaigns(...args);
  getPortalRolloutCampaign: IPortalRolloutApi['getPortalRolloutCampaign'] = (...args) => this.portalRolloutApi.getPortalRolloutCampaign(...args);
  startPortalRolloutCampaign: IPortalRolloutApi['startPortalRolloutCampaign'] = (...args) => this.portalRolloutApi.startPortalRolloutCampaign(...args);
  pausePortalRolloutCampaign: IPortalRolloutApi['pausePortalRolloutCampaign'] = (...args) => this.portalRolloutApi.pausePortalRolloutCampaign(...args);
  resumePortalRolloutCampaign: IPortalRolloutApi['resumePortalRolloutCampaign'] = (...args) => this.portalRolloutApi.resumePortalRolloutCampaign(...args);
  cancelPortalRolloutCampaign: IPortalRolloutApi['cancelPortalRolloutCampaign'] = (...args) => this.portalRolloutApi.cancelPortalRolloutCampaign(...args);
  sendPortalRolloutBatch: IPortalRolloutApi['sendPortalRolloutBatch'] = (...args) => this.portalRolloutApi.sendPortalRolloutBatch(...args);
  resendPortalRolloutCampaign: IPortalRolloutApi['resendPortalRolloutCampaign'] = (...args) => this.portalRolloutApi.resendPortalRolloutCampaign(...args);
  resendPortalRolloutRecipient: IPortalRolloutApi['resendPortalRolloutRecipient'] = (...args) => this.portalRolloutApi.resendPortalRolloutRecipient(...args);
  exportPortalRolloutRecipients: IPortalRolloutApi['exportPortalRolloutRecipients'] = (...args) => this.portalRolloutApi.exportPortalRolloutRecipients(...args);
  getCustomerPortalRollout: IPortalRolloutApi['getCustomerPortalRollout'] = (...args) => this.portalRolloutApi.getCustomerPortalRollout(...args);

  // IBundlesApi (tracker item 85)
  listServiceBundles: IBundlesApi['listServiceBundles'] = (...args) => this.bundlesApi.listServiceBundles(...args);
  getServiceBundle: IBundlesApi['getServiceBundle'] = (...args) => this.bundlesApi.getServiceBundle(...args);
  createServiceBundle: IBundlesApi['createServiceBundle'] = (...args) => this.bundlesApi.createServiceBundle(...args);
  updateServiceBundle: IBundlesApi['updateServiceBundle'] = (...args) => this.bundlesApi.updateServiceBundle(...args);
  deleteServiceBundle: IBundlesApi['deleteServiceBundle'] = (...args) => this.bundlesApi.deleteServiceBundle(...args);
  previewServiceBundle: IBundlesApi['previewServiceBundle'] = (...args) => this.bundlesApi.previewServiceBundle(...args);
  listEnrollments: IBundlesApi['listEnrollments'] = (...args) => this.bundlesApi.listEnrollments(...args);
  listPropertyEnrollments: IBundlesApi['listPropertyEnrollments'] = (...args) => this.bundlesApi.listPropertyEnrollments(...args);
  getEnrollment: IBundlesApi['getEnrollment'] = (...args) => this.bundlesApi.getEnrollment(...args);
  addBundleToProperty: IBundlesApi['addBundleToProperty'] = (...args) => this.bundlesApi.addBundleToProperty(...args);
  updateEnrollmentAutoRenew: IBundlesApi['updateEnrollmentAutoRenew'] = (...args) => this.bundlesApi.updateEnrollmentAutoRenew(...args);
  cancelEnrollment: IBundlesApi['cancelEnrollment'] = (...args) => this.bundlesApi.cancelEnrollment(...args);
  updateEnrollmentStepPrice: IBundlesApi['updateEnrollmentStepPrice'] = (...args) => this.bundlesApi.updateEnrollmentStepPrice(...args);
  getEnrollmentQuote: IBundlesApi['getEnrollmentQuote'] = (...args) => this.bundlesApi.getEnrollmentQuote(...args);
  createRenewal: IBundlesApi['createRenewal'] = (...args) => this.bundlesApi.createRenewal(...args);
  createRenewalsBulk: IBundlesApi['createRenewalsBulk'] = (...args) => this.bundlesApi.createRenewalsBulk(...args);
  listRenewals: IBundlesApi['listRenewals'] = (...args) => this.bundlesApi.listRenewals(...args);
  exportRenewals: IBundlesApi['exportRenewals'] = (...args) => this.bundlesApi.exportRenewals(...args);
  approveEnrollment: IBundlesApi['approveEnrollment'] = (...args) => this.bundlesApi.approveEnrollment(...args);
  declineEnrollment: IBundlesApi['declineEnrollment'] = (...args) => this.bundlesApi.declineEnrollment(...args);
  listPendingEnrollments: IBundlesApi['listPendingEnrollments'] = (...args) => this.bundlesApi.listPendingEnrollments(...args);
  getBundleAlertRouting: IBundlesApi['getBundleAlertRouting'] = (...args) => this.bundlesApi.getBundleAlertRouting(...args);
  updateBundleAlertRouting: IBundlesApi['updateBundleAlertRouting'] = (...args) => this.bundlesApi.updateBundleAlertRouting(...args);

  // IStarterTemplatesApi (tracker item 76)
  listStarterPacks: IStarterTemplatesApi['listStarterPacks'] = (...args) => this.starterApi.listStarterPacks(...args);
  getStarterPack: IStarterTemplatesApi['getStarterPack'] = (...args) => this.starterApi.getStarterPack(...args);
  previewStarterPack: IStarterTemplatesApi['previewStarterPack'] = (...args) => this.starterApi.previewStarterPack(...args);
  applyStarterPack: IStarterTemplatesApi['applyStarterPack'] = (...args) => this.starterApi.applyStarterPack(...args);
  listSeededStarterRecords: IStarterTemplatesApi['listSeededStarterRecords'] = (...args) => this.starterApi.listSeededStarterRecords(...args);
  keepStarterRecord: IStarterTemplatesApi['keepStarterRecord'] = (...args) => this.starterApi.keepStarterRecord(...args);
  removeUnusedStarterRecords: IStarterTemplatesApi['removeUnusedStarterRecords'] = (...args) => this.starterApi.removeUnusedStarterRecords(...args);

  // IRecurringPresetsApi (tracker item 76)
  listRecurringPresets: IRecurringPresetsApi['listRecurringPresets'] = (...args) => this.presetsApi.listRecurringPresets(...args);
  getRecurringPreset: IRecurringPresetsApi['getRecurringPreset'] = (...args) => this.presetsApi.getRecurringPreset(...args);
  createRecurringPreset: IRecurringPresetsApi['createRecurringPreset'] = (...args) => this.presetsApi.createRecurringPreset(...args);
  updateRecurringPreset: IRecurringPresetsApi['updateRecurringPreset'] = (...args) => this.presetsApi.updateRecurringPreset(...args);
  deleteRecurringPreset: IRecurringPresetsApi['deleteRecurringPreset'] = (...args) => this.presetsApi.deleteRecurringPreset(...args);

  // IEstimatesApi (tracker item 39)
  listEstimates: IEstimatesApi['listEstimates'] = (...args) => this.estimatesApi.listEstimates(...args);
  getEstimate: IEstimatesApi['getEstimate'] = (...args) => this.estimatesApi.getEstimate(...args);
  previewEstimate: IEstimatesApi['previewEstimate'] = (...args) => this.estimatesApi.previewEstimate(...args);
  createEstimate: IEstimatesApi['createEstimate'] = (...args) => this.estimatesApi.createEstimate(...args);
  addEstimateBundle: IEstimatesApi['addEstimateBundle'] = (...args) => this.estimatesApi.addEstimateBundle(...args);
  updateEstimateBundle: IEstimatesApi['updateEstimateBundle'] = (...args) => this.estimatesApi.updateEstimateBundle(...args);
  removeEstimateBundle: IEstimatesApi['removeEstimateBundle'] = (...args) => this.estimatesApi.removeEstimateBundle(...args);
  updateEstimate: IEstimatesApi['updateEstimate'] = (...args) => this.estimatesApi.updateEstimate(...args);
  sendEstimate: IEstimatesApi['sendEstimate'] = (...args) => this.estimatesApi.sendEstimate(...args);
  recordEstimateResponse: IEstimatesApi['recordEstimateResponse'] = (...args) => this.estimatesApi.recordEstimateResponse(...args);
  cancelEstimate: IEstimatesApi['cancelEstimate'] = (...args) => this.estimatesApi.cancelEstimate(...args);
  convertEstimate: IEstimatesApi['convertEstimate'] = (...args) => this.estimatesApi.convertEstimate(...args);
  listPortalEstimates: IEstimatesApi['listPortalEstimates'] = (...args) => this.estimatesApi.listPortalEstimates(...args);
  getPortalEstimate: IEstimatesApi['getPortalEstimate'] = (...args) => this.estimatesApi.getPortalEstimate(...args);
  respondPortalEstimate: IEstimatesApi['respondPortalEstimate'] = (...args) => this.estimatesApi.respondPortalEstimate(...args);
  getPublicEstimate: IEstimatesApi['getPublicEstimate'] = (...args) => this.estimatesApi.getPublicEstimate(...args);
  respondPublicEstimate: IEstimatesApi['respondPublicEstimate'] = (...args) => this.estimatesApi.respondPublicEstimate(...args);
}

/**
 * Factory function to create appropriate API client
 * @param useMock - If true, returns MockAPIClient; otherwise RealAPIClient
 * @param baseURL - Base URL for real client (ignored for mock)
 * @returns APIClient instance
 */
export function createAPIClient(
  useMock: boolean = false,
  baseURL: string = process.env.REACT_APP_API_URL || 'http://localhost:5000/api'
): IAPIClient {
  if (useMock) {
    // Will be implemented in mock.ts
    return createMockAPIClient();
  }
  return new RealAPIClient(baseURL);
}

// Placeholder - will be imported from mock.ts
function createMockAPIClient(): IAPIClient {
  throw new Error('Mock API client not yet imported');
}

// Export for use in mock.ts
export function setMockAPIFactory(factory: () => IAPIClient) {
  // This allows mock.ts to register the factory
}
